import React, { createContext, useState, useContext } from 'react';
import Notification from '../ui/components/common/Notification';

// Create context
const NotificationContext = createContext();

/**
 * NotificationProvider component for managing notifications
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const NotificationProvider = ({ children }) => {
  // State for notification
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    type: 'success',
    duration: 5000
  });

  // Show success notification
  const showSuccess = (message, duration = 5000) => {
    setNotification({
      open: true,
      message,
      type: 'success',
      duration
    });
  };

  // Show error notification
  const showError = (message, duration = 5000) => {
    setNotification({
      open: true,
      message,
      type: 'error',
      duration
    });
  };

  // Show warning notification
  const showWarning = (message, duration = 5000) => {
    setNotification({
      open: true,
      message,
      type: 'warning',
      duration
    });
  };

  // Show info notification
  const showInfo = (message, duration = 5000) => {
    setNotification({
      open: true,
      message,
      type: 'info',
      duration
    });
  };

  // Close notification
  const closeNotification = () => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };

  return (
    <NotificationContext.Provider
      value={{
        showSuccess,
        showError,
        showWarning,
        showInfo
      }}
    >
      {children}
      <Notification
        open={notification.open}
        message={notification.message}
        type={notification.type}
        duration={notification.duration}
        onClose={closeNotification}
      />
    </NotificationContext.Provider>
  );
};

/**
 * Hook for using the notification context
 * @returns {Object} Notification context
 */
export const useNotification = () => {
  const context = useContext(NotificationContext);
  
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  
  return context;
};

export default NotificationContext; 